import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  BriefcaseBusiness,
  Building2,
  CalendarClock,
  Camera,
  Check,
  CheckCircle2,
  Clock3,
  Download,
  FileText,
  History,
  Link2,
  MessageSquare,
  PackageCheck,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Signature,
  UserRound,
  X,
} from 'lucide-react';
import { CroatianDateInput } from '../../components/CroatianDateInput';
import { formatDateValue } from '../../lib/croatian-date';
import { useSearchParams } from '../../app/router';
import { readToken, tenantTokenKey } from '../../app/session';
import { SearchableSelect } from '../../components/SearchableSelect';
import { useTranslation } from '../../i18n/i18n';
import { ApiError, apiDownload, apiRequest } from '../../lib/api';
import { useTenantApp } from '../app/TenantAppContext';

interface WorkOrder {
  id: string;
  number: number;
  partnerId: string;
  partnerName: string;
  partnerLocationId: string | null;
  locationName: string | null;
  title: string;
  description: string | null;
  status: string;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  actualStart: string | null;
  actualEnd: string | null;
  customerContactName: string | null;
  assigneeIds: string[];
  assigneeNames: string[];
  itemTotal: number;
  itemDone: number;
  taskIds: string[];
  taskNumbers: number[];
  totalMinutes: number;
  mediaCount: number;
  signedAt: string | null;
}

interface Options {
  partners: Array<{ id: string; code: string; name: string; oib: string | null; city: string }>;
  locations: Array<{ id: string; partnerId: string; name: string }>;
  users: Array<{ id: string; firstName: string; lastName: string }>;
  tasks: Array<{ id: string; number: number; title: string; partnerId: string | null }>;
}

interface OrderDetail {
  id: string;
  number: number;
  partnerId: string;
  partnerName: string;
  partnerLocationId: string;
  locationName: string | null;
  title: string;
  description: string;
  status: string;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  actualStart: string | null;
  actualEnd: string | null;
  customerContactName: string;
  creatorName: string;
  assigneeIds: string[];
  assigneeNames: string[];
  taskId: string;
  items: Array<{ id: string; description: string; quantity: number; unit: string; completed: boolean }>;
  materials: Array<{ id: string; description: string; quantity: number; unit: string }>;
  notes: Array<{ id: string; body: string; authorName: string; createdAt: string }>;
  timeEntries: Array<{ id: string; userId: string; userName: string; workDate: string; minutes: number; description: string; createdAt: string }>;
  media: Array<{ id: string; kind: 'BEFORE' | 'AFTER' | 'ATTACHMENT' | 'SIGNATURE'; filename: string; mimeType: string; sizeBytes: number; createdAt: string; uploadedByName: string; fileUrl: string }>;
  signature: { signerName: string | null; signedAt: string; fileUrl: string } | null;
  history: Array<{ id: string; fromStatus: string | null; toStatus: string; changedAt: string; changedByName: string }>;
}

interface OrderForm {
  partnerId: string;
  taskId: string;
  partnerLocationId: string;
  title: string;
  description: string;
  scheduledStart: string;
  scheduledEnd: string;
  customerContactName: string;
  assigneeIds: string[];
  itemsText: string;
  materialsText: string;
}

const emptyForm: OrderForm = {
  partnerId: '', taskId: '', partnerLocationId: '', title: '', description: '',
  scheduledStart: '', scheduledEnd: '', customerContactName: '', assigneeIds: [],
  itemsText: '', materialsText: '',
};
const statuses = ['ALL', 'DRAFT', 'PLANNED', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'APPROVED', 'CANCELLED'];
const labels: Record<string, string> = {
  DRAFT: 'Skica', PLANNED: 'Planiran', ASSIGNED: 'Dodijeljen',
  IN_PROGRESS: 'U tijeku', COMPLETED: 'Završen', APPROVED: 'Odobren', CANCELLED: 'Otkazan',
};

function localDateTime(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function parseLines(text: string) {
  return text.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
    const [description = '', quantity = '1', unit = 'kom'] = line.split('|').map((part) => part.trim());
    return { description, quantity: Number(quantity) || 1, unit };
  });
}

function readFileDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Datoteku nije moguće pročitati.'));
    reader.readAsDataURL(file);
  });
}

function SignaturePad({ onSave, busy }: { onSave: (dataUrl: string) => void; busy: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const element = canvas.current;
    if (!element) return;
    const resize = () => {
      const rect = element.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      element.width = Math.max(1, Math.round(rect.width * ratio));
      element.height = Math.max(1, Math.round(rect.height * ratio));
      const context = element.getContext('2d');
      context?.setTransform(ratio, 0, 0, ratio, 0, 0);
      if (context) {
        context.lineWidth = 2.3;
        context.lineCap = 'round';
        context.strokeStyle = '#172033';
      }
      setHasInk(false);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const point = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };
  const start = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    drawing.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    const context = event.currentTarget.getContext('2d');
    const next = point(event);
    context?.beginPath();
    context?.moveTo(next.x, next.y);
  };
  const move = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const context = event.currentTarget.getContext('2d');
    const next = point(event);
    context?.lineTo(next.x, next.y);
    context?.stroke();
    setHasInk(true);
  };
  const stop = () => { drawing.current = false; };
  const clear = () => {
    const element = canvas.current;
    const context = element?.getContext('2d');
    if (element && context) context.clearRect(0, 0, element.width, element.height);
    setHasInk(false);
  };
  return (
    <div className="signature-pad">
      <canvas ref={canvas} onPointerDown={start} onPointerMove={move} onPointerUp={stop} onPointerCancel={stop} />
      <div>
        <button type="button" className="button button--ghost button--small" onClick={clear}>Obriši</button>
        <button type="button" className="button button--primary button--small" disabled={!hasInk || busy} onClick={() => { if (canvas.current) onSave(canvas.current.toDataURL('image/png')); }}>Spremi potpis</button>
      </div>
    </div>
  );
}

export function WorkOrdersPage() {
  const { context } = useTenantApp();
  const { t } = useTranslation();
  const canManage = context.user.permissions.includes('work_orders.manage');
  const canExecute = canManage || context.user.permissions.includes('work_orders.execute');
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState<WorkOrder[]>([]);
  const [options, setOptions] = useState<Options>({ partners: [], locations: [], users: [], tasks: [] });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [editor, setEditor] = useState(false);
  const [editing, setEditing] = useState<WorkOrder | null>(null);
  const [form, setForm] = useState<OrderForm>(emptyForm);
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [note, setNote] = useState('');
  const [timeForm, setTimeForm] = useState({ userId: context.user.id, workDate: new Date().toISOString().slice(0, 10), minutes: '60', description: '' });
  const [materialForm, setMaterialForm] = useState({ description: '', quantity: '1', unit: 'kom' });
  const [signerName, setSignerName] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const orderParam = params.get('order');

  async function load() {
    setError('');
    try {
      const query = new URLSearchParams({ search, status });
      if (partnerFilter) query.set('partnerId', partnerFilter);
      const [orders, opts] = await Promise.all([
        apiRequest<{ items: WorkOrder[] }>(`/api/v1/work-orders?${query}`, { accessToken: readToken(tenantTokenKey) }),
        apiRequest<Options>('/api/v1/work-orders/options', { accessToken: readToken(tenantTokenKey) }),
      ]);
      setItems(orders.items);
      setOptions(opts);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Radni nalozi nisu dostupni.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 150);
    return () => window.clearTimeout(timer);
  }, [search, status, partnerFilter]);

  useEffect(() => {
    if (orderParam && detail?.id !== orderParam) void openDetail(orderParam, false);
  }, [orderParam]);

  useEffect(() => {
    const taskId = params.get('taskId');
    if (params.get('new') !== '1' || !taskId || !options.tasks.length || editor) return;
    const task = options.tasks.find((item) => item.id === taskId);
    if (!task) return;
    setEditing(null);
    setForm({ ...emptyForm, taskId: task.id, partnerId: task.partnerId ?? '', title: task.title });
    setEditor(true);
  }, [editor, options.tasks, params]);

  function openNew() {
    setEditing(null);
    setForm(emptyForm);
    setEditor(true);
    setError('');
    setParams({ new: '1' });
  }

  async function openDetail(orderId: string, updateUrl = true) {
    setError('');
    try {
      const response = await apiRequest<OrderDetail>(`/api/v1/work-orders/${orderId}`, {
        accessToken: readToken(tenantTokenKey),
      });
      setDetail(response);
      setSignerName(response.signature?.signerName ?? response.customerContactName);
      setMaterialForm({ description: '', quantity: '1', unit: 'kom' });
      setTimeForm((current) => ({ ...current, userId: response.assigneeIds.includes(context.user.id) ? context.user.id : response.assigneeIds[0] ?? context.user.id }));
      if (updateUrl) setParams({ order: orderId });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Detalji radnog naloga nisu dostupni.');
      if (!updateUrl) setParams({});
    }
  }

  async function openEdit(order: WorkOrder) {
    const response = await apiRequest<OrderDetail>(`/api/v1/work-orders/${order.id}`, {
      accessToken: readToken(tenantTokenKey),
    });
    setEditing(order);
    setForm({
      partnerId: response.partnerId,
      taskId: response.taskId,
      partnerLocationId: response.partnerLocationId,
      title: response.title,
      description: response.description,
      scheduledStart: localDateTime(response.scheduledStart),
      scheduledEnd: localDateTime(response.scheduledEnd),
      customerContactName: response.customerContactName,
      assigneeIds: response.assigneeIds,
      itemsText: response.items.map((item) => `${item.description} | ${item.quantity} | ${item.unit}`).join('\n'),
      materialsText: response.materials.map((item) => `${item.description} | ${item.quantity} | ${item.unit}`).join('\n'),
    });
    setDetail(null);
    setEditor(true);
    setError('');
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await apiRequest(editing ? `/api/v1/work-orders/${editing.id}` : '/api/v1/work-orders', {
        method: editing ? 'PATCH' : 'POST',
        accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({
          ...form,
          scheduledStart: form.scheduledStart ? new Date(form.scheduledStart).toISOString() : '',
          scheduledEnd: form.scheduledEnd ? new Date(form.scheduledEnd).toISOString() : '',
          items: parseLines(form.itemsText),
          materials: parseLines(form.materialsText),
        }),
      });
      setEditor(false);
      setEditing(null);
      setParams({});
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Radni nalog nije moguće spremiti.');
    } finally {
      setBusy(false);
    }
  }

  async function changeStatus(next: string) {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      await apiRequest(`/api/v1/work-orders/${detail.id}/status`, {
        method: 'PATCH', accessToken: readToken(tenantTokenKey), body: JSON.stringify({ status: next }),
      });
      await openDetail(detail.id, false);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Status nije moguće promijeniti.');
    } finally { setBusy(false); }
  }

  async function toggleItem(item: OrderDetail['items'][number]) {
    if (!detail) return;
    await apiRequest(`/api/v1/work-orders/${detail.id}/items/${item.id}`, {
      method: 'PATCH', accessToken: readToken(tenantTokenKey), body: JSON.stringify({ completed: !item.completed }),
    });
    await openDetail(detail.id, false);
    await load();
  }

  async function addNote(event: FormEvent) {
    event.preventDefault();
    if (!detail || !note.trim()) return;
    setBusy(true);
    try {
      await apiRequest(`/api/v1/work-orders/${detail.id}/notes`, {
        method: 'POST', accessToken: readToken(tenantTokenKey), body: JSON.stringify({ body: note }),
      });
      setNote('');
      await openDetail(detail.id, false);
    } finally { setBusy(false); }
  }

  async function addTime(event: FormEvent) {
    event.preventDefault();
    if (!detail) return;
    setBusy(true);
    try {
      await apiRequest(`/api/v1/work-orders/${detail.id}/time-entries`, {
        method: 'POST', accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({ ...timeForm, minutes: Number(timeForm.minutes), userId: timeForm.userId }),
      });
      setTimeForm({ ...timeForm, minutes: '60', description: '' });
      await openDetail(detail.id, false);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Sate nije moguće evidentirati.');
    } finally { setBusy(false); }
  }

  async function addMaterial(event: FormEvent) {
    event.preventDefault();
    if (!detail || !materialForm.description.trim()) return;
    setBusy(true);
    setError('');
    try {
      await apiRequest(`/api/v1/work-orders/${detail.id}/materials`, {
        method: 'POST', accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({
          description: materialForm.description,
          quantity: Number(materialForm.quantity),
          unit: materialForm.unit,
        }),
      });
      setMaterialForm({ description: '', quantity: '1', unit: 'kom' });
      await openDetail(detail.id, false);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Materijal nije moguće evidentirati.');
    } finally { setBusy(false); }
  }

  async function uploadMedia(file: File, kind: 'BEFORE' | 'AFTER' | 'ATTACHMENT') {
    if (!detail) return;
    setBusy(true);
    try {
      await apiRequest(`/api/v1/work-orders/${detail.id}/media`, {
        method: 'POST', accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({ originalFilename: file.name, fileDataUrl: await readFileDataUrl(file), kind }),
      });
      await openDetail(detail.id, false);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Datoteku nije moguće spremiti.');
    } finally { setBusy(false); }
  }

  async function openFile(url: string) {
    const { blob } = await apiDownload(url, readToken(tenantTokenKey));
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  }

  async function saveSignature(dataUrl: string) {
    if (!detail || signerName.trim().length < 2) {
      setError('Prije potpisa upišite ime potpisnika.');
      return;
    }
    setBusy(true);
    try {
      await apiRequest(`/api/v1/work-orders/${detail.id}/signature`, {
        method: 'PUT', accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({ signerName, signatureDataUrl: dataUrl }),
      });
      await openDetail(detail.id, false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Potpis nije moguće spremiti.');
    } finally { setBusy(false); }
  }

  async function downloadPdf() {
    if (!detail) return;
    const { blob, filename } = await apiDownload(`/api/v1/work-orders/${detail.id}/pdf`, readToken(tenantTokenKey));
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const locations = options.locations.filter((location) => location.partnerId === form.partnerId);
  const partnerOptions = useMemo(() => options.partners.map((partner) => ({
    value: partner.id, label: `${partner.code} · ${partner.name}`,
    searchText: `${partner.name} ${partner.code} ${partner.oib ?? ''} ${partner.city}`,
    description: [partner.oib ? `OIB ${partner.oib}` : '', partner.city].filter(Boolean).join(' · '),
  })), [options.partners]);
  const taskOptions = useMemo(() => options.tasks.map((task) => ({
    value: task.id, label: `#${task.number} · ${task.title}`,
    searchText: `${task.number} ${task.title}`,
    description: task.partnerId ? options.partners.find((partner) => partner.id === task.partnerId)?.name : 'Interni zadatak',
  })), [options.partners, options.tasks]);
  const formatDate = (value: string | null) => value ? new Date(value).toLocaleString('hr-HR') : '—';
  const duration = (minutes: number) => `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
  const operationallyEditable = Boolean(detail && ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED'].includes(detail.status));

  return (
    <div className="module-page work-orders-page">
      <header className="module-page__hero">
        <div><span className="eyebrow">OPERATIVA · TEREN</span><h1>{t('workOrders.title')}</h1><p>{t('workOrders.subtitle')}</p></div>
        {canManage && <button className="button button--primary" data-mobile-fab onClick={openNew} aria-label="Novi radni nalog"><Plus /> Novi radni nalog</button>}
      </header>
      <section className="task-summary">
        <article><BriefcaseBusiness /><div><strong>{items.filter((item) => !['APPROVED', 'CANCELLED'].includes(item.status)).length}</strong><small>Otvorenih naloga</small></div></article>
        <article><CheckCircle2 /><div><strong>{items.filter((item) => item.status === 'COMPLETED').length}</strong><small>Čeka odobrenje</small></div></article>
        <article><Clock3 /><div><strong>{duration(items.reduce((sum, item) => sum + item.totalMinutes, 0))}</strong><small>Evidentirano rada</small></div></article>
      </section>
      <section className="data-card">
        <header className="data-toolbar">
          <label className="compact-search"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Broj, partner ili opis…" /></label>
          <SearchableSelect className="searchable-select--compact" value={partnerFilter} options={partnerOptions} onChange={setPartnerFilter} placeholder="Svi partneri" searchPlaceholder="Naziv, šifra, OIB ili mjesto…" allowClear ariaLabel="Filtriraj po partneru" />
          <select className="compact-select" value={status} onChange={(event) => setStatus(event.target.value)}>{statuses.map((item) => <option key={item} value={item}>{item === 'ALL' ? 'Svi statusi' : labels[item]}</option>)}</select>
        </header>
        {error && !detail && !editor && <div className="form-error">{error}</div>}
        <div className="operational-list">
          {items.map((order) => (
            <article className="work-card mobile-record-card" key={order.id} role="button" tabIndex={0} onClick={() => void (canManage && ['DRAFT', 'PLANNED', 'ASSIGNED'].includes(order.status) ? openEdit(order) : openDetail(order.id))} onKeyDown={(event) => { if (event.key === 'Enter') void (canManage && ['DRAFT', 'PLANNED', 'ASSIGNED'].includes(order.status) ? openEdit(order) : openDetail(order.id)); }}>
              <div className="work-card__number">RN-{order.number}</div>
              <div className="work-card__body">
                <div><strong>{order.title}</strong><span className={`status-pill status-pill--${order.status.toLowerCase()}`}>{labels[order.status]}</span>{order.taskNumbers.map((number) => <span className="linked-record" key={number}><Link2 /> Zadatak #{number}</span>)}</div>
                <p><Building2 /> {order.partnerName}{order.locationName ? ` · ${order.locationName}` : ''}</p>
                <footer><span><UserRound /> {order.assigneeNames.join(', ')}</span><span><CalendarClock /> {formatDate(order.scheduledStart)}</span><span><CheckCircle2 /> {order.itemDone}/{order.itemTotal}</span><span><Clock3 /> {duration(order.totalMinutes)}</span>{order.mediaCount > 0 && <span><Camera /> {order.mediaCount}</span>}{order.signedAt && <span><Signature /> Potpisano</span>}</footer>
              </div>
              <div className="stack-actions">
                {canManage && ['DRAFT', 'PLANNED', 'ASSIGNED'].includes(order.status) && <button className="icon-button" onClick={(event) => { event.stopPropagation(); void openEdit(order); }}><Pencil /></button>}
                <button className="button button--small button--ghost" onClick={(event) => { event.stopPropagation(); void openDetail(order.id); }}>Detalji</button>
              </div>
            </article>
          ))}
          {!loading && !items.length && <div className="table-empty">Nema radnih naloga u ovom prikazu.</div>}
        </div>
      </section>

      {editor && <div className="modal-backdrop"><form className="operational-modal work-order-editor" onSubmit={save}>
        <header><div><span className="eyebrow">RADNI NALOG</span><h2>{editing ? `Uredi RN-${editing.number}` : 'Novi radni nalog'}</h2></div><button type="button" className="icon-button" onClick={() => { setEditor(false); setParams({}); }}><X /></button></header>
        <div className="modal-scroll"><div className="form-grid">
          <div className="field field--full"><span>Povezani zadatak</span><SearchableSelect value={form.taskId} options={taskOptions} onChange={(taskId) => { const task = options.tasks.find((item) => item.id === taskId); setForm({ ...form, taskId, partnerId: form.partnerId || task?.partnerId || '' }); }} placeholder="Nije vezano uz zadatak" searchPlaceholder="Broj ili naziv…" allowClear /></div>
          <div className="field"><span>Partner</span><SearchableSelect value={form.partnerId} options={partnerOptions} onChange={(partnerId) => setForm({ ...form, partnerId, partnerLocationId: '' })} placeholder="Odaberi partnera" searchPlaceholder="Naziv, šifra, OIB ili mjesto…" /></div>
          <label className="field"><span>Lokacija</span><select value={form.partnerLocationId} onChange={(event) => setForm({ ...form, partnerLocationId: event.target.value })}><option value="">Glavna adresa</option>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select></label>
          <label className="field field--full"><span>Naslov</span><input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></label>
          <label className="field field--full"><span>Opis posla</span><textarea rows={4} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
          <label className="field"><span>Planirani početak</span><CroatianDateInput withTime value={form.scheduledStart} onValueChange={(value) => setForm({ ...form, scheduledStart: value })} ariaLabel="Planirani početak" /></label>
          <label className="field"><span>Planirani završetak</span><CroatianDateInput withTime value={form.scheduledEnd} onValueChange={(value) => setForm({ ...form, scheduledEnd: value })} min={form.scheduledStart} ariaLabel="Planirani završetak" /></label>
          <label className="field field--full"><span>Kontakt kod naručitelja</span><input value={form.customerContactName} onChange={(event) => setForm({ ...form, customerContactName: event.target.value })} /></label>
          <fieldset className="assignee-picker field--full"><legend>Izvršitelji</legend><div>{options.users.map((user) => <label key={user.id} className={form.assigneeIds.includes(user.id) ? 'is-selected' : ''}><input type="checkbox" checked={form.assigneeIds.includes(user.id)} onChange={() => setForm({ ...form, assigneeIds: form.assigneeIds.includes(user.id) ? form.assigneeIds.filter((id) => id !== user.id) : [...form.assigneeIds, user.id] })} /><span className="avatar avatar--soft">{user.firstName[0]}{user.lastName[0]}</span><strong>{user.firstName} {user.lastName}</strong></label>)}</div></fieldset>
          <label className="field field--full"><span>Stavke rada (opis | količina | jedinica)</span><textarea rows={5} value={form.itemsText} onChange={(event) => setForm({ ...form, itemsText: event.target.value })} placeholder={'Montaža uređaja | 1 | kom\nTestiranje sustava | 2 | sat'} /></label>
          <label className="field field--full"><span>Planirani materijal (opis | količina | jedinica)</span><textarea rows={5} value={form.materialsText} onChange={(event) => setForm({ ...form, materialsText: event.target.value })} placeholder={'Kabel UTP | 25 | m\nKonektor | 4 | kom'} /></label>
        </div>{error && <div className="form-error">{error}</div>}</div>
        <footer><button type="button" className="button button--ghost" onClick={() => { setEditor(false); setParams({}); }}>Odustani</button><button className="button button--primary" disabled={busy || !form.assigneeIds.length || !form.partnerId}>{busy ? 'Spremam…' : 'Spremi i obavijesti izvršitelje'}</button></footer>
      </form></div>}

      {detail && <div className="modal-backdrop"><section className="operational-modal work-order-detail">
        <header><div><span className="eyebrow">RADNI NALOG RN-{detail.number}</span><h2>{detail.title}</h2><p>{detail.partnerName}{detail.locationName ? ` · ${detail.locationName}` : ''}</p></div><button className="icon-button" onClick={() => { setDetail(null); setParams({}); }}><X /></button></header>
        <div className="modal-scroll">
          <div className="work-order-detail__actions">
            <span className={`status-pill status-pill--${detail.status.toLowerCase()}`}>{labels[detail.status]}</span>
            {canExecute && detail.status === 'ASSIGNED' && <button className="button button--primary" disabled={busy} onClick={() => void changeStatus('IN_PROGRESS')}>Pokreni rad</button>}
            {canExecute && detail.status === 'IN_PROGRESS' && <button className="button button--primary" disabled={busy || detail.items.some((item) => !item.completed)} onClick={() => void changeStatus('COMPLETED')}>Završi nalog</button>}
            {canManage && detail.status === 'COMPLETED' && <button className="button button--primary" disabled={busy} onClick={() => void changeStatus('APPROVED')}>Odobri nalog</button>}
            <button className="button button--ghost" onClick={() => void downloadPdf()}><Download /> PDF</button>
          </div>
          <div className="work-order-facts">
            <span><CalendarClock /><small>Planirano</small><strong>{formatDate(detail.scheduledStart)}</strong></span>
            <span><UserRound /><small>Izvršitelji</small><strong>{detail.assigneeNames.join(', ')}</strong></span>
            <span><Clock3 /><small>Ukupno rada</small><strong>{duration(detail.timeEntries.reduce((sum, entry) => sum + entry.minutes, 0))}</strong></span>
          </div>
          {detail.description && <p className="work-order-description">{detail.description}</p>}
          <div className="work-order-detail-grid">
            <section className="task-detail-panel"><header><h3><CheckCircle2 /> Stavke rada</h3><small>{detail.items.filter((item) => item.completed).length}/{detail.items.length}</small></header><div className="detail-checklist">{detail.items.map((item) => <button key={item.id} className={item.completed ? 'is-completed' : ''} disabled={!canExecute || !['ASSIGNED', 'IN_PROGRESS'].includes(detail.status)} onClick={() => void toggleItem(item)}><span>{item.completed ? <Check /> : <span />}</span><strong>{item.description}</strong><small>{item.quantity} {item.unit}</small></button>)}</div></section>
            <section className="task-detail-panel"><header><h3><PackageCheck /> Materijal</h3><small>{detail.materials.length}</small></header><div className="material-list">{detail.materials.map((material) => <span key={material.id}><strong>{material.description}</strong><em>{material.quantity} {material.unit}</em></span>)}{!detail.materials.length && <p className="muted">Nema evidentiranog materijala.</p>}</div>{canExecute && operationallyEditable && <form className="material-entry-form" onSubmit={addMaterial}><input required value={materialForm.description} onChange={(event) => setMaterialForm({ ...materialForm, description: event.target.value })} placeholder="Utrošeni materijal" /><input type="number" required min="0.001" step="0.001" value={materialForm.quantity} onChange={(event) => setMaterialForm({ ...materialForm, quantity: event.target.value })} placeholder="Količina" /><input required value={materialForm.unit} onChange={(event) => setMaterialForm({ ...materialForm, unit: event.target.value })} placeholder="Jedinica" /><button className="button button--primary button--small" disabled={busy}>Dodaj</button></form>}</section>
            <section className="task-detail-panel"><header><h3><Clock3 /> Evidencija sati</h3><small>{duration(detail.timeEntries.reduce((sum, entry) => sum + entry.minutes, 0))}</small></header><div className="time-entry-list">{detail.timeEntries.map((entry) => <article key={entry.id}><strong>{entry.userName}</strong><span>{formatDateValue(entry.workDate)} · {duration(entry.minutes)}</span>{entry.description && <p>{entry.description}</p>}</article>)}</div>{canExecute && operationallyEditable && <form className="time-entry-form" onSubmit={addTime}>{canManage && <select value={timeForm.userId} onChange={(event) => setTimeForm({ ...timeForm, userId: event.target.value })}>{detail.assigneeIds.map((id, index) => <option key={id} value={id}>{detail.assigneeNames[index] ?? id}</option>)}</select>}<CroatianDateInput required value={timeForm.workDate} onValueChange={(value) => setTimeForm({ ...timeForm, workDate: value })} ariaLabel="Datum rada" /><input type="number" min="1" max="1440" required value={timeForm.minutes} onChange={(event) => setTimeForm({ ...timeForm, minutes: event.target.value })} placeholder="Minute" /><input value={timeForm.description} onChange={(event) => setTimeForm({ ...timeForm, description: event.target.value })} placeholder="Opis rada" /><button className="button button--primary button--small" disabled={busy}>Dodaj sate</button></form>}</section>
            <section className="task-detail-panel"><header><h3><Camera /> Fotografije i privitci</h3><small>{detail.media.filter((item) => item.kind !== 'SIGNATURE').length}</small></header><div className="detail-files">{detail.media.filter((item) => item.kind !== 'SIGNATURE').map((media) => <button key={media.id} onClick={() => void openFile(media.fileUrl)}>{media.kind === 'ATTACHMENT' ? <Paperclip /> : <Camera />}<span><strong>{media.kind === 'BEFORE' ? 'Prije · ' : media.kind === 'AFTER' ? 'Poslije · ' : ''}{media.filename}</strong><small>{media.uploadedByName} · {(media.sizeBytes / 1024).toFixed(0)} KB</small></span></button>)}</div>{canExecute && operationallyEditable && <div className="detail-upload-actions"><label className="button button--ghost button--small"><Camera /> Fotografija prije<input type="file" accept="image/*" capture="environment" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadMedia(file, 'BEFORE'); event.target.value = ''; }} /></label><label className="button button--ghost button--small"><Camera /> Fotografija poslije<input type="file" accept="image/*" capture="environment" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadMedia(file, 'AFTER'); event.target.value = ''; }} /></label><label className="button button--ghost button--small"><Paperclip /> Privitak<input type="file" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadMedia(file, 'ATTACHMENT'); event.target.value = ''; }} /></label></div>}</section>
            <section className="task-detail-panel"><header><h3><MessageSquare /> Napomene</h3><small>{detail.notes.length}</small></header><div className="detail-comments">{detail.notes.map((entry) => <article key={entry.id}><strong>{entry.authorName}</strong><time>{formatDate(entry.createdAt)}</time><p>{entry.body}</p></article>)}</div>{canExecute && operationallyEditable && <form className="detail-comment-form" onSubmit={addNote}><textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Napomena s terena…" /><button className="button button--primary button--small" disabled={busy || !note.trim()}>Objavi</button></form>}</section>
            <section className="task-detail-panel"><header><h3><Signature /> Potpis naručitelja</h3><small>{detail.signature ? 'Evidentiran' : 'Nedostaje'}</small></header>{detail.signature ? <div className="signature-confirmed"><CheckCircle2 /><span><strong>{detail.signature.signerName}</strong><small>{formatDate(detail.signature.signedAt)}</small></span><button className="button button--ghost button--small" onClick={() => void openFile(detail.signature!.fileUrl)}>Otvori potpis</button></div> : canExecute && operationallyEditable ? <><label className="field"><span>Ime potpisnika</span><input value={signerName} onChange={(event) => setSignerName(event.target.value)} /></label><SignaturePad busy={busy} onSave={(dataUrl) => void saveSignature(dataUrl)} /></> : <p className="muted">Potpis još nije evidentiran.</p>}</section>
            <section className="task-detail-panel"><header><h3><History /> Povijest statusa</h3><small>{detail.history.length}</small></header><div className="detail-history">{detail.history.map((entry) => <article key={entry.id}><span /><div><strong>{entry.fromStatus ? `${labels[entry.fromStatus]} → ` : ''}{labels[entry.toStatus]}</strong><small>{entry.changedByName} · {formatDate(entry.changedAt)}</small></div></article>)}</div></section>
          </div>
          {error && <div className="form-error">{error}</div>}
        </div>
        <footer><span>Kreirao {detail.creatorName}</span><div>{canManage && ['DRAFT', 'PLANNED', 'ASSIGNED'].includes(detail.status) && <button className="button button--ghost" onClick={() => { const summary = items.find((item) => item.id === detail.id); if (summary) void openEdit(summary); }}>Uredi nalog</button>}<button className="button button--primary" onClick={() => void downloadPdf()}><FileText /> Preuzmi PDF</button></div></footer>
      </section></div>}
    </div>
  );
}
